import {isArray, isString, keyBy} from 'lodash';
import React, {useCallback, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {useTaxonomy} from 'src/common/hooks/useTaxonomy';
import {themmeColor} from 'src/constants/constants';
import DSAccordion from 'src/DesignSystem/Accordion/DSAccordion';
import Box from 'src/DesignSystem/Box/Box';
import DSButton from 'src/DesignSystem/Button/Button';
import {ArrayChildProps} from 'src/DesignSystem/Form/common/ArrayField/ArrayField';
import {useArrayField} from 'src/DesignSystem/Form/common/ArrayField/useArrayField';
import Text from 'src/DesignSystem/Form/common/Text/Text';
import {
  FONT_WEIGHTS,
  TEXT_TYPES,
} from 'src/DesignSystem/Form/common/Text/Text.types';
import {Form} from 'src/DesignSystem/Form/Form';
import {
  FORM_INPUT_TYPE,
  FormInputProps,
} from 'src/DesignSystem/Form/Form.types';
import {createEmptyFormDataFromFormConfig} from 'src/DesignSystem/Form/Form.utils';
import {
  OverWriteFormFieldsFunctionType,
  useFormData,
} from 'src/DesignSystem/Form/useFormData';
import DeleteIcon from 'src/icons/DeleteIcon';
import DocumentIcon from 'src/icons/DocumentIcon';
import ManualIcon from 'src/icons/ManualIcon';
import {selectBusinessDetails} from 'src/store/businessDetails/businessDetails.selector';
import {
  BusinessDetailsField,
  BusinessType,
} from 'src/store/businessDetails/businessDetails.types';
import {
  InformationSource,
  TaxFormFieldIds,
} from 'src/store/taxForms/taxForms.types';
import {overWriteProfitLossExpenseHeader} from 'src/store/taxForms/taxForms.utils';

interface ExpenseItemProps extends ArrayChildProps {
  entityType: BusinessType;
  irsCategories: string[];
  getL2s: ReturnType<typeof useTaxonomy>['getL2s'];
}

const ExpenseItem = ({
  childTitle,
  array,
  arrayObjectIndex,
  onChangeValue,
  childProps,
  entityType,
  irsCategories,
  getL2s,
}: ExpenseItemProps) => {
  const {arrayObject, updateObject} = useArrayField(
    array,
    onChangeValue,
    arrayObjectIndex,
  );

  const overWriteFields: OverWriteFormFieldsFunctionType = useCallback(
    (props) => {
      return overWriteProfitLossExpenseHeader({
        ...props,
        irsCategories,
        getL2s,
        entityType,
      });
    },
    [entityType, irsCategories, getL2s],
  );

  const {fields} = useFormData({
    data: arrayObject,
    setData: updateObject,
    config: childProps,
    overWriteFields,
  });

  const isApplicableSwitchField = fields.find(
    (field) => field.path === TaxFormFieldIds.IS_APPLICABLE,
  );

  const onDelete = () => {
    const newValue = array.filter((_, index) => index !== arrayObjectIndex);
    onChangeValue(newValue);
  };

  return (
    <Box
      borderColor={themmeColor.silver}
      col
      style={{
        padding: 16,
        marginBottom: 12,
      }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
        <div style={{display: 'flex', gap: 4, alignItems: 'center'}}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: themmeColor.grey,
              border: `1px solid ${themmeColor.offWhite}`,
              padding: '2px 8px',
            }}>
            <Text
              color={themmeColor.white}
              type={TEXT_TYPES.S}
              fontWeight={FONT_WEIGHTS.SemiBold}
              text={arrayObjectIndex + 1}
            />
          </div>

          {arrayObject[TaxFormFieldIds.SOURCE] ===
          InformationSource.document ? (
            <DocumentIcon />
          ) : (
            <ManualIcon />
          )}
        </div>
        <div style={{display: 'flex', gap: 8}}>
          {arrayObject[TaxFormFieldIds.SOURCE] !==
          InformationSource.document ? (
            <DSButton
              text="Delete"
              startIcon={<DeleteIcon />}
              type="secondary"
              onClick={onDelete}
            />
          ) : null}
          {isApplicableSwitchField ? (
            <Form {...isApplicableSwitchField} />
          ) : null}
        </div>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '0px 12px',
          marginTop: 12,
        }}>
        {fields.map((field) => {
          if (
            [TaxFormFieldIds.SOURCE, TaxFormFieldIds.IS_APPLICABLE].includes(
              field.path as TaxFormFieldIds,
            )
          ) {
            return null;
          }
          return (
            <div style={{marginBottom: 12}}>
              <Form {...field} />
            </div>
          );
        })}
      </div>
    </Box>
  );
};

const ProfitLossExpensesHeader = ({
  bizId,
  ...props
}: FormInputProps & {bizId: number}) => {
  const {businesses} = useSelector(selectBusinessDetails);

  const {irsCategories, getL2s} = useTaxonomy();

  const bizEntityType = useMemo(() => {
    return (
      businesses.find((biz) => biz[BusinessDetailsField.id] === bizId)?.[
        BusinessDetailsField.entity_type
      ] ?? BusinessType.S_CORP
    );
  }, [businesses, bizId]);

  if (props.inputType !== FORM_INPUT_TYPE.Array) {
    return null;
  }

  const onAddExpenseItem = () => {
    const oldValue = Array.isArray(props.value) ? props.value : [];
    const newItem = createEmptyFormDataFromFormConfig(props.childProps, 0);
    newItem[TaxFormFieldIds.SOURCE] = InformationSource.manual;
    newItem[TaxFormFieldIds.IS_APPLICABLE] = true;
    const newValue = [newItem, ...oldValue];
    props.onChangeValue(newValue);
  };

  return (
    <div style={{marginTop: 12, marginBottom: 12}}>
      <DSAccordion defaultExpanded name={props.title}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
        }}>
        <DSButton
          type="primary"
          onClick={onAddExpenseItem}
          text={`Add ${props.title}`}
          style={{marginLeft: 'auto'}}
        />
      </div>
      {Array.isArray(props.value)
        ? props.value.map((_, i) => (
            <div>
              <ExpenseItem
                array={props.value}
                arrayObjectIndex={i}
                childProps={props.childProps}
                onChangeValue={props.onChangeValue}
                disableValidation={props.disableValidation}
                childTitle={props.childTitle}
                entityType={bizEntityType}
                irsCategories={irsCategories}
                getL2s={getL2s}
              />
            </div>
          ))
        : null}
        </DSAccordion>
    </div>
  );
};

export default ProfitLossExpensesHeader;
